/**
 * Jam masuk kerja per wilayah — K134, diisi Kepala Cabang.
 *
 * Owner 31 Agustus 2026: *"dikirim otomatis tiap [pagi] 15 menit setelah jam
 * masuk. Jam masuk diisi oleh kepala cabang masing masing region, bisa
 * dimasukan dalam kelompok HRD."*
 *
 * ---------------------------------------------------------------------------
 * ⛔ DUA HAL YANG LAYAR INI WAJIB TUNJUKKAN
 * ---------------------------------------------------------------------------
 *
 *  1. **Jam KIRIM, bukan cuma jam masuk.** Yang diisi orang adalah jam masuk;
 *     yang berakibat adalah jam kirim (`+15 menit`). Layar yang cuma
 *     menampilkan yang diisi membuat orang menebak akibatnya — dan tebakan itu
 *     baru ketahuan salah waktu tugas sampai di jam yang tidak diduga.
 *
 *     ⚠ Angkanya datang dari BACKEND, nol dihitung di sini. Jeda 15 menit itu
 *     keputusan K134 yang hidup di `jadwal_kirim_tugas.JEDA_MENIT`;
 *     menyalinnya ke layar berarti layar menjanjikan jam yang bukan jam
 *     sebenarnya begitu jedanya berubah.
 *
 *  2. **Zona waktu wilayahnya.** WIB dan WITA beda satu jam, dan "08:15" tanpa
 *     zona adalah dua waktu yang berbeda. Menyembunyikannya membuat Owner yang
 *     mengurus dua wilayah membandingkan dua angka yang nol sebanding.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";

// Nama zona waktu IANA → sebutan yang dikenal staf.
const ZONA = { "Asia/Jakarta": "WIB", "Asia/Pontianak": "WIB", "Asia/Makassar": "WITA", "Asia/Jayapura": "WIT" };

export default function HRJamMasuk() {
  const [items, setItems] = useState([]);
  const [bisaUbah, setBisaUbah] = useState(false);
  const [muat, setMuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [kabar, setKabar] = useState("");
  const [draf, setDraf] = useState({});
  const [sibuk, setSibuk] = useState(null);

  const muatData = useCallback(async () => {
    setMuat(true);
    setGalat("");
    try {
      const { data } = await api.get("/hr/jam-masuk");
      setItems(data.items || []);
      setBisaUbah(!!data.bisa_ubah);
      const awal = {};
      (data.items || []).forEach((x) => {
        awal[x.id] = (x.jam_masuk || "08:00:00").slice(0, 5);
      });
      setDraf(awal);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setMuat(false);
    }
  }, []);

  useEffect(() => {
    muatData();
  }, [muatData]);

  const simpan = async (region) => {
    setSibuk(region.id);
    setGalat("");
    setKabar("");
    try {
      const { data } = await api.put("/hr/jam-masuk", {
        jam_masuk: draf[region.id],
      }, { params: { region_id: region.id } });
      setKabar(
        `${data.name}: jam masuk ${data.jam_masuk.slice(0, 5)}, ` +
        `tugas harian dikirim ${data.jam_kirim} waktu setempat.`
      );
      await muatData();
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setSibuk(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Jam Kerja</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tugas harian dikirim otomatis ke Telegram staf, 15 menit sesudah jam
          masuk wilayahnya.
        </p>
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}
      {kabar && (
        <div className="rounded-lg border border-success-soft-border bg-success-soft px-4 py-3 text-sm text-success-soft-foreground">
          {kabar}
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}

      {!muat && items.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Tidak ada wilayah yang bisa Anda atur.
        </div>
      )}

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium text-foreground">{r.name}</p>
                {/* ⚠ Zona waktunya DITAMPILKAN. "08:15" tanpa zona adalah dua
                    waktu berbeda di WIB dan WITA. */}
                <p className="text-sm text-muted-foreground">{ZONA[r.timezone] || r.timezone || "—"}</p>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-foreground">
                    Jam masuk
                  </span>
                  <input
                    type="time"
                    disabled={!bisaUbah}
                    className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground disabled:opacity-50"
                    value={draf[r.id] || ""}
                    onChange={(e) =>
                      setDraf((d) => ({ ...d, [r.id]: e.target.value }))
                    }
                  />
                </label>

                {/* ⛔ AKIBATNYA ditampilkan di sebelah sebabnya, bukan di
                    halaman bantuan. Yang mengisi jam masuk sedang memutuskan
                    jam kirim — dua angka itu wajib terlihat bersamaan. */}
                <div className="pb-2">
                  <span className="block text-xs text-muted-foreground">
                    Tugas dikirim
                  </span>
                  <span className="tabular-nums text-lg font-semibold text-foreground">
                    {r.jam_kirim}
                  </span>
                </div>

                {bisaUbah && (
                  <button
                    type="button"
                    disabled={
                      sibuk === r.id ||
                      (draf[r.id] || "") === (r.jam_masuk || "").slice(0, 5)
                    }
                    onClick={() => simpan(r)}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                  >
                    {sibuk === r.id ? "Menyimpan…" : "Simpan"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ⚠ Batasnya disebut DI MUKA, bukan cuma saat ditolak. Aturan yang
          hanya muncul sebagai pesan galat memaksa orang menemukannya dengan
          gagal lebih dulu. */}
      {bisaUbah && !muat && items.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Jam masuk wajib antara 04:00 dan 12:00 — kiriman tugas dini hari
          sampai sebelum ada yang bangun, lalu tenggelam sebelum jam kerja mulai.
        </p>
      )}
    </div>
  );
}
